import type { OrgRole, Permission } from './types'

/**
 * The pure logic behind the person panel (pages/People.tsx) that is worth
 * testing on its own: what the "All branches" checkbox starts as, and which
 * roles a caller may hand out.
 */

/**
 * Whether a caller may hand out a role: every permission it gives is in
 * `mayGive`, the server's own answer to what this caller may give (GET
 * /permissions). The server's mayAssign (internal/api/staff.go) checks the same
 * against the role's effective set. For the locked role that is every
 * organisation permission, which the server now sends as its permission list
 * (store scanRole) — so the locked role is givable by whoever holds them all,
 * an organisation's own administrator included, not by the owner alone.
 */
export function roleGivable(role: OrgRole, mayGive: readonly Permission[]): boolean {
  return role.permissions.every((p) => mayGive.includes(p))
}

/**
 * Whether "All branches" starts ticked.
 *
 * Only for an existing person whose branch list is already empty — which is
 * already what an empty list means on the server (every branch). A brand new
 * person always starts unticked, with nothing chosen: `branchIds` is absent
 * for one too, exactly like an existing person who is in every branch, so
 * `isNew` has to be checked on its own rather than inferred from the list
 * being empty — otherwise a new person's form would open with every branch
 * silently selected before anybody had chosen anything.
 */
export function initialAllBranches(isNew: boolean, branchIds: readonly string[] | undefined): boolean {
  return !isNew && (branchIds ?? []).length === 0
}
